import { useMemo } from "react";
import { useAdminLinkMonitor } from "@/hooks/useAdminLinkMonitor";
import { useAdminProducts } from "@/hooks/useAdminStore";

export type NotificationSeverity = "error" | "warning" | "info";

export type AdminNotification = {
  id: string;
  type: "broken_link" | "no_link" | "low_stock" | "expired_deal";
  severity: NotificationSeverity;
  title: string;
  description: string;
  itemId: number;
  itemName: string;
  itemType: "product" | "deal";
  link: string;
};

function isValidUrl(url: string): boolean {
  if (!url || url === "#") return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function isPlaceholder(url: string): boolean {
  return !url || url === "#" || url.trim() === "";
}

export function useAdminNotifications() {
  const { products } = useAdminProducts();
  const { brokenRecords } = useAdminLinkMonitor();

  const notifications = useMemo<AdminNotification[]>(() => {
    const result: AdminNotification[] = [];
    const monitoredIssueKeys = new Set(brokenRecords.map((record) => `${record.itemType}:${record.itemId}`));
    const dailyDealProducts = products.filter((product) => product.showInDailyDeals);

    brokenRecords.forEach((record) => {
      result.push({
        id: `monitor-${record.key}`,
        type: record.status === "missing" ? "no_link" : "broken_link",
        severity: record.status === "timeout" || record.status === "error" ? "warning" : "error",
        title: record.status === "missing" ? "Item sem link monitorado" : "Link com falha na varredura",
        description: `${record.itemName}: ${record.detail}`,
        itemId: record.itemId,
        itemName: record.itemName,
        itemType: record.itemType,
        link: "/admin/relatorios?tab=broken-links",
      });
    });

    products.forEach((product) => {
      const key = `product:${product.id}`;
      if (monitoredIssueKeys.has(key)) return;

      if (isPlaceholder(product.affiliateLink)) {
        result.push({
          id: `product-no-link-${product.id}`,
          type: "no_link",
          severity: "error",
          title: "Produto sem link de afiliado",
          description: `"${product.name}" não tem link de afiliado configurado.`,
          itemId: product.id,
          itemName: product.name,
          itemType: "product",
          link: "/admin/links",
        });
      } else if (!isValidUrl(product.affiliateLink)) {
        result.push({
          id: `product-broken-link-${product.id}`,
          type: "broken_link",
          severity: "warning",
          title: "Link de afiliado inválido",
          description: `"${product.name}" tem um link com formato inválido: ${product.affiliateLink}`,
          itemId: product.id,
          itemName: product.name,
          itemType: "product",
          link: "/admin/links",
        });
      }
    });

    dailyDealProducts.forEach((product) => {
      const key = `deal:${product.id}`;
      if (monitoredIssueKeys.has(key)) return;

      if (isPlaceholder(product.affiliateLink)) {
        result.push({
          id: `deal-no-link-${product.id}`,
          type: "no_link",
          severity: "error",
          title: "Oferta do Dia sem link de afiliado",
          description: `"${product.name}" está habilitado em Ofertas do Dia, mas não tem link configurado.`,
          itemId: product.id,
          itemName: product.name,
          itemType: "deal",
          link: "/admin/links",
        });
      } else if (!isValidUrl(product.affiliateLink)) {
        result.push({
          id: `deal-broken-link-${product.id}`,
          type: "broken_link",
          severity: "warning",
          title: "Link inválido em Oferta do Dia",
          description: `"${product.name}" está habilitado em Ofertas do Dia com um link em formato inválido.`,
          itemId: product.id,
          itemName: product.name,
          itemType: "deal",
          link: "/admin/links",
        });
      }
    });

    const order: Record<NotificationSeverity, number> = { error: 0, warning: 1, info: 2 };
    return result.sort((a, b) => order[a.severity] - order[b.severity]);
  }, [brokenRecords, products]);

  const errorCount = notifications.filter((notification) => notification.severity === "error").length;
  const warningCount = notifications.filter((notification) => notification.severity === "warning").length;
  const infoCount = notifications.filter((notification) => notification.severity === "info").length;
  const totalCount = notifications.length;

  return { notifications, errorCount, warningCount, infoCount, totalCount };
}
